import { localPoint } from '@visx/event';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChartContext, type ChartContextValue } from './context';
import {
  approxTextWidth,
  MARKER_LABEL_PAD,
  nearestIndex,
  placeMarkerLabels,
  tickCount,
  valueDomain,
  type IndexRange,
} from './math';
import { useElementSize } from './use-element-size';

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const DEFAULT_MARGIN: ChartMargin = { top: 12, right: 16, bottom: 28, left: 64 };

/** Axis label size on screen (px) and in print (pt: print charts are laid out in points). */
const SCREEN_FONT_SIZE = 11;
const PRINT_FONT_SIZE = 7;

export interface ChartEvent {
  index: number;
  label: string;
  tone?: 'neutral' | 'critical' | 'positive';
}

/** A named month drawn as a labelled vertical line across the plot (e.g. a key date). */
export interface ChartMarker {
  /** Stable and unique among the markers (labels and months may repeat). */
  id: string;
  index: number;
  label: string;
}

/** Marker labels take up to this many rows above the plot. */
const MARKER_ROWS = 2;

export interface ChartFrameProps {
  /** Shared time axis: one Date per step (UTC month starts). */
  dates: readonly Date[];
  height: number;
  /** Monthly series used to compute the y-domain within `range` (lines, stack extents). */
  domainSeries?: readonly ArrayLike<number>[];
  /** Explicit y-domain, for data that isn't monthly (e.g. yearly bars). Overrides domainSeries. */
  yDomain?: readonly [number, number] | undefined;
  formatY: (value: number) => string;
  formatXTick: (date: Date) => string;
  /** Accessible summary of what the chart shows. */
  ariaLabel: string;
  /** Visible window of month indexes (zoom); the whole axis when omitted. */
  range?: IndexRange | undefined;
  margin?: Partial<ChartMargin>;
  /** Tooltip body for the hovered step. */
  renderTooltip?: (index: number) => ReactNode;
  /** Controlled hover (for crosshair sync across charts); uncontrolled when omitted. */
  hoverIndex?: number;
  onHoverChange?: (index: number) => void;
  /** Click / Enter on a month (e.g. to explain it). */
  onSelectIndex?: ((index: number) => void) | undefined;
  /** Markers on the time axis (one-offs, payoffs, first negative cash…). */
  events?: readonly ChartEvent[] | undefined;
  /** Labelled vertical lines; their labels take room above the plot. */
  markers?: readonly ChartMarker[] | undefined;
  /** Fixed width; measured from the container when omitted. Required with `print`. */
  width?: number;
  /**
   * Static output for print and export: a bare `<svg>` with no focus, hover, crosshair or tooltip,
   * hairline grid lines and smaller axis labels. Render it with `renderToStaticMarkup`.
   */
  print?: boolean;
  /** Axis label size; 11 on screen, 7 in print. */
  fontSize?: number;
  /** Drawn above the plot without clipping, with the chart context (e.g. `EndLabels`). */
  annotations?: ReactNode;
  children: ReactNode;
}

const EVENT_COLOR: Record<NonNullable<ChartEvent['tone']>, string> = {
  neutral: 'var(--chart-axis-label)',
  critical: 'var(--status-critical)',
  positive: 'var(--status-good)',
};

/**
 * Responsive time-series frame: scales, grid, axes, crosshair, tooltip, keyboard navigation and
 * event markers. Layers are children and read the scales from context. Colours come from CSS
 * variables, so themes need no chart code.
 *
 * Keyboard: when focused, ←/→ move the crosshair a month (⇧ a year), Home/End jump, Enter selects.
 */
export function ChartFrame({
  dates,
  height,
  domainSeries,
  yDomain,
  formatY,
  formatXTick,
  ariaLabel,
  range,
  margin: marginProp,
  renderTooltip,
  hoverIndex: controlledHover,
  onHoverChange,
  onSelectIndex,
  events,
  markers,
  width: fixedWidth,
  print = false,
  fontSize = print ? PRINT_FONT_SIZE : SCREEN_FONT_SIZE,
  annotations,
  children,
}: ChartFrameProps) {
  const clipId = useId();
  const [containerRef, measured] = useElementSize<HTMLDivElement>();
  const width = fixedWidth ?? measured.width;
  const hairline = print ? 0.5 : 1;
  const [localHover, setLocalHover] = useState(-1);
  const hoverIndex = print ? -1 : (controlledHover ?? localHover);
  const setHover = (i: number): void => {
    if (controlledHover === undefined) setLocalHover(i);
    onHoverChange?.(i);
  };

  const from = Math.max(0, range?.[0] ?? 0);
  const to = Math.min(dates.length - 1, range?.[1] ?? dates.length - 1);

  const shownMarkers = (markers ?? []).filter((m) => m.index >= from && m.index <= to);
  const markerRows = Math.min(MARKER_ROWS, shownMarkers.length);
  const markerRow = fontSize + 4;
  const baseMargin = {
    ...DEFAULT_MARGIN,
    top: DEFAULT_MARGIN.top + markerRows * markerRow,
    bottom: fontSize + 17,
  };
  const innerHeight = Math.max(0, height - baseMargin.top - baseMargin.bottom);
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: yDomain ? [...yDomain] : valueDomain(domainSeries ?? [], { range: [from, to] }),
        range: [innerHeight, 0],
        nice: true,
      }),
    [domainSeries, yDomain, innerHeight, from, to],
  );
  const yTicks = yScale.ticks(Math.max(2, Math.floor(innerHeight / 48)));
  const yLabels = yTicks.map(formatY);
  const labelWidth = Math.max(0, ...yLabels.map((l) => approxTextWidth(l, fontSize))) + 14;
  const margin = { ...baseMargin, left: Math.max(40, Math.ceil(labelWidth)), ...marginProp };
  const innerWidth = Math.max(0, width - margin.left - margin.right);

  const xScale = useMemo(() => {
    const first = dates[from] ?? new Date(0);
    const last = dates[to] ?? first;
    return scaleUtc<number>({ domain: [first, last], range: [0, innerWidth] });
  }, [dates, from, to, innerWidth]);

  const ctx = useMemo<ChartContextValue>(
    () => ({ dates, xScale, yScale, innerWidth, innerHeight, hoverIndex, range: [from, to] }),
    [dates, xScale, yScale, innerWidth, innerHeight, hoverIndex, from, to],
  );

  const xTicks = xScale.ticks(tickCount(innerWidth));
  const markerXs = shownMarkers.map((m) => {
    const date = dates[m.index];
    return date ? xScale(date) : 0;
  });
  const markerLabels = placeMarkerLabels(
    shownMarkers.map((m, i) => ({
      x: markerXs[i] ?? 0,
      width: approxTextWidth(m.label, fontSize),
    })),
    innerWidth,
    markerRows,
  );

  const indexAt = (
    event: React.PointerEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
  ): number => {
    const point = localPoint(event);
    if (!point) return -1;
    const time = xScale.invert(point.x - margin.left).getTime();
    return Math.min(to, Math.max(from, nearestIndex(dates, time)));
  };

  const hoverDate = hoverIndex >= from && hoverIndex <= to ? dates[hoverIndex] : undefined;
  const hoverX = hoverDate ? xScale(hoverDate) : 0;
  const tooltipOnLeft = hoverX > innerWidth * 0.6;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = hoverIndex >= from && hoverIndex <= to ? hoverIndex : from;
    const step = event.shiftKey ? 12 : 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = Math.min(to, current + step);
    else if (event.key === 'ArrowLeft') next = Math.max(from, current - step);
    else if (event.key === 'Home') next = from;
    else if (event.key === 'End') next = to;
    else if (event.key === 'Enter' && onSelectIndex && hoverDate) {
      event.preventDefault();
      onSelectIndex(current);
      return;
    }
    if (next !== null) {
      event.preventDefault();
      event.stopPropagation();
      setHover(next);
    }
  };

  const svg = width > 0 && (
    <svg width={width} height={height} role="img" aria-label={ariaLabel}>
      <defs>
        <clipPath id={clipId}>
          <rect width={innerWidth} height={innerHeight + 1} />
        </clipPath>
      </defs>
      <g transform={`translate(${String(margin.left)},${String(margin.top)})`}>
        <g aria-hidden>
          {yTicks.map((tick, i) => (
            <g key={tick} transform={`translate(0,${String(yScale(tick))})`}>
              <line
                x2={innerWidth}
                style={{ stroke: 'var(--chart-grid)' }}
                strokeWidth={hairline}
                shapeRendering="crispEdges"
              />
              <text
                x={-10}
                dy="0.32em"
                textAnchor="end"
                className="tabular"
                style={{ fill: 'var(--chart-axis-label)', fontSize }}
              >
                {yLabels[i]}
              </text>
            </g>
          ))}
          <line
            y1={innerHeight}
            y2={innerHeight}
            x2={innerWidth}
            style={{ stroke: 'var(--chart-axis)' }}
            strokeWidth={hairline}
            shapeRendering="crispEdges"
          />
          {xTicks.map((tick) => (
            <text
              key={tick.getTime()}
              x={xScale(tick)}
              y={innerHeight + fontSize + 7}
              textAnchor="middle"
              className="tabular"
              style={{ fill: 'var(--chart-axis-label)', fontSize }}
            >
              {formatXTick(tick)}
            </text>
          ))}
          {shownMarkers.map((m, i) => {
            const x = markerXs[i] ?? 0;
            const place = markerLabels[i];
            const top = place ? -(place.row + 1) * markerRow : 0;
            return (
              <g key={m.id}>
                <line
                  x1={x}
                  x2={x}
                  y1={top}
                  y2={innerHeight}
                  style={{ stroke: 'var(--chart-axis-label)' }}
                  strokeWidth={hairline}
                  strokeDasharray="2 3"
                  opacity={0.7}
                  shapeRendering="crispEdges"
                />
                {place && (
                  <text
                    x={place.anchor === 'start' ? x + MARKER_LABEL_PAD : x - MARKER_LABEL_PAD}
                    y={top + fontSize}
                    textAnchor={place.anchor}
                    style={{ fill: 'var(--chart-axis-label)', fontSize }}
                  >
                    {m.label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
        <ChartContext.Provider value={ctx}>
          <g clipPath={`url(#${clipId})`}>{children}</g>
          {annotations}
        </ChartContext.Provider>
        {hoverDate && (
          <line
            aria-hidden
            x1={hoverX}
            x2={hoverX}
            y2={innerHeight}
            style={{ stroke: 'var(--chart-crosshair)' }}
            strokeDasharray="3 3"
            shapeRendering="crispEdges"
          />
        )}
        {!print && (
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            style={onSelectIndex ? { cursor: 'pointer' } : undefined}
            onPointerMove={(e) => {
              setHover(indexAt(e));
            }}
            onPointerLeave={() => {
              setHover(-1);
            }}
            onClick={(e) => {
              const i = indexAt(e);
              if (onSelectIndex && i >= 0) onSelectIndex(i);
            }}
          />
        )}
        {events
          ?.filter((e) => e.index >= from && e.index <= to)
          .map((e) => {
            const date = dates[e.index];
            if (!date) return null;
            return (
              <g
                key={`${String(e.index)}-${e.label}`}
                transform={`translate(${String(xScale(date))},${String(innerHeight)})`}
              >
                {!print && <title>{e.label}</title>}
                <path
                  d="M0,-4.5 L4.5,0 L0,4.5 L-4.5,0 Z"
                  style={{
                    fill: EVENT_COLOR[e.tone ?? 'neutral'],
                    stroke: 'var(--chart-surface)',
                  }}
                  strokeWidth={1.5}
                />
              </g>
            );
          })}
      </g>
    </svg>
  );

  if (print) return svg;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onBlur={() => {
        setHover(-1);
      }}
      className="relative w-full rounded-md outline-none select-none focus-visible:ring-2 focus-visible:ring-focus"
      style={{ height }}
    >
      {svg}
      {hoverDate && renderTooltip && (
        <div
          role="tooltip"
          className="pointer-events-none absolute top-2 z-10 min-w-44 rounded-md bg-bg-2 px-2.5 py-2 text-xs shadow-popover"
          style={
            tooltipOnLeft
              ? { right: width - (margin.left + hoverX) + 12 }
              : { left: margin.left + hoverX + 12 }
          }
        >
          {renderTooltip(hoverIndex)}
        </div>
      )}
    </div>
  );
}
